/* ================================================================
PART 1: VIDEO PLAYER LOGIC (MODIFIED TO AVOID CONFLICT)
================================================================
*/

const MUTE_ICON = `<svg class="muted-icon" viewBox="0 0 24 24"><path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"></path></svg>`;
const UNMUTE_ICON = `<svg class="unmuted-icon" viewBox="0 0 24 24"><path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"></path></svg>`;

// Global functions that can be called by connector
function initializeGalleryVideo(container) {
  if (container.dataset.playerInitialized === 'true' || container.dataset.playerInitializing === 'true') return;
  const videoId = container.dataset.videoId;
  if (!videoId) return;

  container.dataset.playerInitializing = 'true'; 
  createGalleryFacade(container, videoId);
}

function createGalleryFacade(container, videoId) {
  container.innerHTML = `
    <div class="youtube-player-facade">
        <img src="https://img.youtube.com/vi/${videoId}/maxresdefault.jpg" alt="Video thumbnail" loading="lazy" onerror="this.onerror=null;this.src='https://placehold.co/1920x1080/000/FFF?text=Error+Loading+Thumbnail';this.alt='Error loading video thumbnail';">
        <div class="youtube-overlay-button youtube-play-button"></div>
    </div>
  `;
  container.dataset.playerInitialized = 'false';
  container.dataset.playerInitializing = 'false';
  container.ytPlayer = null; 

  // This listener is for the FACADE click, not the thumbnail.
  // This is used for the "swipe, then tap-to-play" on mobile.
  container.querySelector('.youtube-player-facade').addEventListener('click', () => {
    createGalleryPlayer(container, videoId, { 
      autoplay: true,
      controls: true,
      isGallery: true,
      forceUnmute: false // This will play muted, as is correct for an autoplay
    });
  }, { once: true });
}

function createGalleryPlayer(container, videoId, options) {
  if (container.dataset.playerInitialized === 'true') {
    return;
  }
  container.dataset.playerInitializing = 'true';
  const playerDiv = document.createElement('div');
  container.innerHTML = ''; 
  container.appendChild(playerDiv);
  
  // Gallery player is ALWAYS muted by default
  let muteValue = 1;
  const player = new YT.Player(playerDiv, {
    videoId: videoId,
    playerVars: { 
      'rel': 0, 
      'playsinline': 1,
      'autoplay': 1,
      'mute': muteValue,
      'controls': options.controls ? 1 : 0
    },
    events: {
      'onReady': (event) => {
        container.ytPlayer = event.target;
        container.dataset.playerInitialized = 'true';
        container.dataset.playerInitializing = 'false';

        if (muteValue === 1) {
            createGalleryMuteButton(container, event.target);
        }

        // --- SCROLL OBSERVER (AUTO-RESUME LOGIC) ---
        // Only start observing when player is ready
        if (!container.videoObserver) {
            const observer = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    const yt = container.ytPlayer;
                    // Safety check to ensure player exists and methods are available
                    if (!yt || typeof yt.playVideo !== 'function') return;

                    if (entry.isIntersecting) {
                        // Visible > 50%: Play (Auto-Resume)
                        // We check getPlayerState to avoid spamming 'play' commands
                        if(yt.getPlayerState() !== 1) { // 1 = Playing
                             yt.playVideo();
                        }
                    } else {
                        // Hidden: Pause
                         if(yt.getPlayerState() === 1) {
                             yt.pauseVideo();
                         }
                    }
                });
            }, { threshold: 0.5 }); // 50% visibility threshold

            observer.observe(container);
            container.videoObserver = observer;
        }
        // --- END OBSERVER ---
      },
      'onStateChange': (event) => {
        if (event.data === YT.PlayerState.ENDED) {
          // CLEANUP OBSERVER
          if (container.videoObserver) {
              container.videoObserver.disconnect();
              delete container.videoObserver;
          }
          container.ytPlayer = null;
          event.target.destroy();
          createGalleryFacade(container, videoId);
        }
      },
      'onError': (event) => {
        console.warn(`Custom Video: YouTube player error for video ID "${videoId}". Video may be private, deleted, or the ID is incorrect.`, event.data);
        // CLEANUP OBSERVER
        if (container.videoObserver) {
            container.videoObserver.disconnect();
            delete container.videoObserver;
        }
        container.ytPlayer = null;
        try {
          event.target.destroy();
        } catch (e) { /* ignore */ }
        createGalleryFacade(container, videoId);
      }
    }
  });
}

function createGalleryMuteButton(container, player) {
  const oldButton = container.querySelector('.youtube-mute-button');
  if (oldButton) oldButton.remove();
  const button = document.createElement('button');
  button.className = 'youtube-mute-button';
  button.innerHTML = MUTE_ICON + UNMUTE_ICON;
  
  if (player.isMuted()) {
    button.classList.remove('unmuted');
  } else {
    button.classList.add('unmuted');
  }

  container.appendChild(button);
  button.addEventListener('click', (e) => {
    e.stopPropagation(); 
    if (player.isMuted()) { 
      player.unMute(); 
      button.classList.add('unmuted'); 
    } else { 
      player.mute(); 
      button.classList.remove('unmuted'); 
    }
  });
}

// --- API LOADING (MODIFIED) ---
// FIX: Renamed to be a non-global-colliding function
function runGalleryVideoInit() {
    // FIX: Only select videos INSIDE the media gallery
    console.log('Gallery Video: Running runGalleryVideoInit...');
    document.querySelectorAll('media-gallery .youtube-video-container').forEach(initializeGalleryVideo);
    
    // FIX: Call the connector setup *after* the facades are initialized.
    // This ensures the click/swipe listeners are attached.
    setupGalleryVideoConnector();
};
function loadGalleryYouTubeAPI() {
    if (!window.YT) {
        // Set a flag to show this script is responsible for loading
        if (!window.isYouTubeApiLoading) {
            console.log('Gallery Video: Loading YouTube API...');
            window.isYouTubeApiLoading = true;
            const tag = document.createElement('script');
            tag.src = "https://www.youtube.com/iframe_api";
            const firstScriptTag = document.getElementsByTagName('script')[0];
            firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
        }
    } else if (window.YT.Player) {
        // If API is already loaded, just run this script's initialization
        console.log('Gallery Video: API already loaded, initializing.');
        runGalleryVideoInit();
    }

    // FIX: This is the proper way to hook into the API
    // Store the original ready function (if it exists from another script)
    const originalOnYouTubeIframeAPIReady = window.onYouTubeIframeAPIReady;
    // Define (or redefine) the global ready function
    window.onYouTubeIframeAPIReady = () => {
        console.log('Gallery Video: onYouTubeIframeAPIReady fired.');
        // Call the original function first (if it was defined by the standalone script)
        originalOnYouTubeIframeAPIReady?.();
        // Now, run this script's initialization
        runGalleryVideoInit();
    };
}
// Run on initial load
document.addEventListener('DOMContentLoaded', loadGalleryYouTubeAPI);
// FIX: Also run on Shopify's "Turbo" navigation
document.addEventListener('turbo:load', loadGalleryYouTubeAPI);
// Run on Shopify theme editor load
document.addEventListener('shopify:section:load', function(event) {
  setTimeout(loadGalleryYouTubeAPI, 100);
});
/* ================================================================
PART 2: GALLERY CONNECTOR SCRIPT (MODIFIED)
This code connects your script to the Dawn theme's media gallery
================================================================
*/

// --- MASTER "STOP" FUNCTION ---
function stopAllGalleryVideos(gallery) {
  if (!gallery) gallery = document.querySelector('media-gallery');
  if (!gallery) return;

  // FIX: Be specific to the gallery container
  const activePlayers = gallery.querySelectorAll('.youtube-video-container[data-player-initialized="true"]');
  console.log(`Custom Video: Found ${activePlayers.length} players to stop.`);

  activePlayers.forEach(container => {
    if (container.ytPlayer) {
      // CLEANUP OBSERVER
      if (container.videoObserver) {
          container.videoObserver.disconnect();
          delete container.videoObserver;
      }
      try {
        container.ytPlayer.stopVideo();
        container.ytPlayer.destroy();
      } catch (e) { /* ignore */ }
      
      container.ytPlayer = null; 
      const videoId = container.dataset.videoId;
      if (videoId) {
        createGalleryFacade(container, videoId); // Rebuild the facade
      }
    }
  });
}

// --- MASTER "PLAY" FUNCTION ---
function playVideoInSlide(slideElement) {
  if (!slideElement) return;
  const videoContainer = slideElement.querySelector('.youtube-video-container[data-video-id]');
  if (videoContainer) {
    console.log('Custom Video: Target slide IS a video. Checking player status.');
    if (videoContainer.dataset.playerInitialized !== 'true' && videoContainer.dataset.playerInitializing !== 'true') {
      const videoId = videoContainer.dataset.videoId;
      if (videoId) {
        console.log('Custom Video: Creating new player...');
        // Use the gallery-specific player function
        createGalleryPlayer(videoContainer, videoId, {
          autoplay: true, 
          controls: true, 
          isGallery: true
        });
      }
    } else if (videoContainer.ytPlayer) {
       console.log('Custom Video: Player already exists, just playing.');
       videoContainer.ytPlayer.playVideo();
    }
  } else {
    console.log('Custom Video: Target slide is NOT a video.');
  }
}

function setupGalleryVideoConnector() {
  console.log('Custom Video: Running setupGalleryVideoConnector...');
  
  const mediaGallery = document.querySelector('media-gallery');
  if (!mediaGallery) {
    console.log('Custom Video: FAILED. Could not find <media-gallery> element.');
    return;
  }
  
  if (mediaGallery.dataset.videoConnectorInitialized === 'true') {
    console.log('Custom Video: Connector already initialized. Skipping.');
    return;
  }
  mediaGallery.dataset.videoConnectorInitialized = 'true';

  // --- 1. Listen for SLIDING (This works for mobile) ---
  const sliderComponent = mediaGallery.querySelector('slider-component[id^="GalleryViewer-"]');
  if (sliderComponent) {
    sliderComponent.addEventListener('slideChanged', (event) => {
      console.log('Custom Video: slideChanged event FIRED.');
      
      // Stop video in the old slide
      if (event.detail.previousElement) {
        const oldVideoContainer = event.detail.previousElement.querySelector('.youtube-video-container[data-player-initialized="true"]');
        if (oldVideoContainer && oldVideoContainer.ytPlayer) {
          // CLEANUP OBSERVER
          if (oldVideoContainer.videoObserver) {
              oldVideoContainer.videoObserver.disconnect();
              delete oldVideoContainer.videoObserver;
          }
          try {
            oldVideoContainer.ytPlayer.stopVideo();
            oldVideoContainer.ytPlayer.destroy();
          } catch(e) {}
          oldVideoContainer.ytPlayer = null;
          const videoId = oldVideoContainer.dataset.videoId;
          if (videoId) createGalleryFacade(oldVideoContainer, videoId); // use gallery-specific facade
        }
      }
      
      // --- MOBILE FIX (This is the correct, stable version) ---
      // We do not attempt to autoplay on swipe, because mobile browsers
      // block it. This creates the "swipe, then tap to play" behavior.
      playVideoInSlide(event.detail.currentElement);
    });
    console.log('Custom Video: Attached slideChanged listener.');
  } else {
    console.log('Custom Video: FAILED. Could not find slider-component.');
  }

  // --- 2. Listen for THUMBNAIL CLICKS (This is the fix for desktop) ---
  const thumbnailList = mediaGallery.querySelector('.thumbnail-list');
  if (thumbnailList) {
    thumbnailList.addEventListener('click', (event) => {
      const clickedButton = event.target.closest('button.thumbnail');
      if (!clickedButton) return; 
      
      console.log('Custom Video: Thumbnail CLICKED.');
      
      const newSlideTargetId = clickedButton.closest('li').dataset.target;
      const newSlide = mediaGallery.querySelector(`#Slide-${newSlideTargetId}`);
      
      if (!newSlide) {
        console.log('Custom Video: Could not find target slide. Aborting.');
        return;
      }
          
      const videoContainerInNewSlide = newSlide.querySelector('.youtube-video-container[data-video-id]');

      if (videoContainerInNewSlide) {
        // --- This is a VIDEO thumbnail ---
        
        if (videoContainerInNewSlide.ytPlayer) {
          console.log('Custom Video: Clicked active video. No action.');
          return;
        }

        console.log('Custom Video: Clicked NEW video thumbnail. Playing...');
        stopAllGalleryVideos(mediaGallery);
        // This call is now in the same event stack as the user's click,
        // which satisfies the browser's autoplay policy.
        playVideoInSlide(newSlide);

      } else {
        // --- This is an IMAGE thumbnail ---
        console.log('Custom Video: Clicked IMAGE thumbnail. Stopping all videos.');
        stopAllGalleryVideos(mediaGallery);
      }
    });
    console.log('Custom Video: Attached CLICK listener to thumbnail list.');
  } else {
    console.log('Custom Video: Could not find any .thumbnail-list.');
  }
}

// Re-run connector setup for theme editor
document.addEventListener('shopify:section:load', () => {
  // We need to re-run the *whole* setup, including API loading
  setTimeout(() => {
    loadGalleryYouTubeAPI();
  }, 100);
});